import Phaser from "phaser";
import {
    trackEvent,
    trackFileDownload,
    type AnalyticsSource,
} from "../analytics.js";
import { applyBetterCrt } from "../effects/crt.js";
import { type PasswordPromptConfig } from "../shell/denied-commands.js";
import { preloadTerminalAudio, TerminalAudio } from "../effects/terminal-audio.js";
import { BootSequenceRunner } from "../render/boot-sequence.js";
import { TerminalSession } from "../shell/session.js";
import { VfsSession } from "../shell/vfs.js";
import type { AppData, Command } from "../types.js";
import { handleTerminalCommand } from "./command-handler.js";
import { triggerBrowserDownload } from "../shell/browser-download.js";
import { trackCommand } from "../telemetry.js";
import {
    COLORS,
    FONT,
    FONT_SIZE,
    GAME_HEIGHT,
    GAME_WIDTH,
    PROFILE_TEXTURE_KEY,
    REBOOT_COUNTDOWN,
} from "./constants.js";
import { TerminalImageDisplay } from "./image-display.js";
import { formatShellPrompt, TerminalInputChrome } from "./input-chrome.js";
import { TerminalOutputView } from "./output-view.js";

export class TerminalScene extends Phaser.Scene {
    private commandBuffer = "";
    private passwordBuffer = "";
    private passwordAttempts = 0;
    private passwordConfig: PasswordPromptConfig | null = null;
    private appData!: AppData;
    private vfs!: VfsSession;
    private session = new TerminalSession();
    private commands!: Record<string, Command>;
    private outputContainer!: Phaser.GameObjects.Container;
    private poweringUpText?: Phaser.GameObjects.Text;
    private terminalAudio!: TerminalAudio;
    private soundsLoaded = false;
    private bootSequenceStarted = false;
    private awaitingPressAnyKey = false;
    private userGestureReceived = false;
    private loopSuspended = false;
    private inputEnabled = false;
    private rebootPending = false;
    private bootRunner = new BootSequenceRunner();
    private outputView!: TerminalOutputView;
    private inputChrome!: TerminalInputChrome;
    private images!: TerminalImageDisplay;
    private wheelHandler = (event: WheelEvent): void => {
        event.preventDefault();
        this.outputView.onWheel(event.deltaY);
    };

    constructor() {
        super("TerminalScene");
    }

    create(): void {
        this.appData = this.registry.get("appData") as AppData;
        this.vfs = new VfsSession(this.appData.vfs, this.appData.config.home);
        this.terminalAudio = new TerminalAudio(this);
        this.terminalAudio.bindUnlock(() => {
            if (!this.awaitingPressAnyKey) {
                return;
            }

            this.userGestureReceived = true;
            this.resumeLoop();
            this.startBootWhenReady();
        });
        this.commands = {
            clear: "clear",
            whoami: "whoami",
        };

        this.outputContainer = this.add.container(0, 0);

        const runCommand = (command: string, source: AnalyticsSource): void => {
            this.runCommand(command, source);
        };

        this.inputChrome = new TerminalInputChrome(this, {
            playClick: () => this.terminalAudio.playClick(),
            runCommand,
            getShellPrompt: () => this.shellPrompt(),
        });

        this.outputView = new TerminalOutputView(
            this,
            this.outputContainer,
            {
                playClick: () => this.terminalAudio.playClick(),
                runCommand,
            },
            () => this.scale.width,
            () => this.inputChrome.promptTop(this.scale.height, this.inputEnabled),
        );

        this.images = new TerminalImageDisplay(
            this,
            this.outputContainer,
            this.terminalAudio,
            () => this.outputView.nextLineY,
            (y) => {
                this.outputView.nextLineY = y;
            },
            (value) => {
                this.outputView.scrollPinBottom = value;
            },
            () => this.outputView.scrollOutput(),
            () => this.outputView.outputMaxWidth(),
            this.appData.profileImage,
            this.appData.config.profileRedaction,
        );

        this.inputChrome.createPrompt();
        this.inputChrome.createStatusBar(this.appData.config.statusBar);
        this.inputChrome.setVisible(false);
        this.input.keyboard!.on("keydown", this.onKeyDown, this);
        this.game.canvas.addEventListener("wheel", this.wheelHandler, { passive: false });

        this.loadSoundsAndStart();
        this.layout();
        applyBetterCrt(this);
    }

    shutdown(): void {
        this.game.canvas.removeEventListener("wheel", this.wheelHandler);
    }

    private showPoweringUp(showPressKey = false): void {
        const preBoot = this.appData.boot.preBoot;
        const lines = [preBoot?.poweringUp ?? "Powering up..."];
        if (showPressKey) {
            lines.push(preBoot?.pressAnyKey ?? "Press any key to continue...");
        }

        this.poweringUpText?.destroy();
        this.poweringUpText = this.add
            .text(GAME_WIDTH / 2, GAME_HEIGHT / 2, lines.join("\n"), {
                fontFamily: FONT,
                fontSize: FONT_SIZE,
                color: COLORS.dim,
                align: "center",
                lineSpacing: 8,
            })
            .setOrigin(0.5, 0.5)
            .setDepth(500);
    }

    private hidePoweringUp(): void {
        this.poweringUpText?.destroy();
        this.poweringUpText = undefined;
    }

    private loadSoundsAndStart(): void {
        this.showPoweringUp();

        if (
            this.cache.audio.exists("boot") &&
            this.cache.audio.exists("loop") &&
            this.cache.audio.exists("click") &&
            this.cache.audio.exists("key") &&
            this.cache.audio.exists("error") &&
            this.cache.audio.exists("read")
        ) {
            this.onSoundsReady();
            return;
        }

        preloadTerminalAudio(this.load);
        this.load.image(PROFILE_TEXTURE_KEY, this.appData.profileImage);
        this.load.once(Phaser.Loader.Events.COMPLETE, () => this.onSoundsReady());
        this.load.start();
    }

    private onSoundsReady(): void {
        this.soundsLoaded = true;
        this.startBootWhenReady();
    }

    private suspendLoop(): void {
        if (this.loopSuspended) return;
        this.loopSuspended = true;
        requestAnimationFrame(() => {
            if (this.loopSuspended) {
                this.game.loop.sleep();
            }
        });
    }

    private resumeLoop(): void {
        if (!this.loopSuspended) return;
        this.loopSuspended = false;
        this.game.loop.wake();
    }

    private startBootWhenReady(): void {
        if (!this.soundsLoaded || this.bootSequenceStarted) {
            return;
        }

        if (!this.userGestureReceived) {
            this.awaitingPressAnyKey = true;
            this.showPoweringUp(true);
            this.suspendLoop();
            return;
        }

        if (this.sound.locked) {
            return;
        }

        if (this.awaitingPressAnyKey) {
            trackEvent("press_any_key");
            this.awaitingPressAnyKey = false;
        }

        this.resumeLoop();
        this.beginBoot();
    }

    private clearScreen(): void {
        this.exitPasswordMode();
        this.images.stopScanlineReveal();
        this.terminalAudio.resetPictureRead();
        this.outputView.clear();
        this.bootRunner.reset();
    }

    private reboot(): void {
        if (this.rebootPending) return;
        this.rebootPending = true;
        this.inputEnabled = false;

        for (const n of [3, 2, 1]) {
            this.time.delayedCall((3 - n) * 1000, () => {
                this.outputView.appendLine(REBOOT_COUNTDOWN.replace("{n}", String(n)), {
                    color: COLORS.dim,
                });
            });
        }

        this.time.delayedCall(3000, () => {
            this.rebootPending = false;
            this.performReboot();
        });
    }

    private performReboot(): void {
        this.time.removeAllEvents();
        this.outputView.buffer.reset();
        this.bootRunner.reset();
        this.terminalAudio.reset();
        this.commandBuffer = "";
        this.bootSequenceStarted = false;
        this.inputEnabled = false;
        this.vfs = new VfsSession(this.appData.vfs, this.appData.config.home);
        this.session.reset();
        this.clearScreen();
        this.inputChrome.setVisible(false);
        this.hidePoweringUp();
        this.beginBoot();
    }

    private beginBoot(): void {
        if (this.bootSequenceStarted) return;
        this.bootSequenceStarted = true;
        trackEvent("boot_start");

        this.hidePoweringUp();
        this.bootRunner.run(
            this.appData.boot,
            {
                boot: this.appData.boot,
                config: this.appData.config,
            },
            {
                appendBanner: (text) => this.outputView.appendBanner(text),
                appendLine: (text, style, instant) =>
                    this.outputView.appendLine(text, style, instant),
                setAnimation: (animation) => this.outputView.buffer.setAnimation(animation),
                runCommand: (command, source) => this.runCommand(command, source),
                enableInput: () => this.finishBoot(),
                playSound: (key, volume) => this.terminalAudio.playSound(key, volume),
                tryPlayBoot: (callbacks) => this.terminalAudio.tryPlayBoot(callbacks),
                ensureLoop: () => this.terminalAudio.ensureLoop(),
                getBootDurationMs: () => this.terminalAudio.getBootDurationMs(),
                schedule: (delayMs, fn) => this.time.delayedCall(delayMs, fn),
                resolveStyle: (name) => this.outputView.bootStyle(name),
                isInputEnabled: () => this.inputEnabled,
            },
        );
    }

    private finishBoot(): void {
        this.inputEnabled = true;
        this.inputChrome.setVisible(true);
        this.layout();
        trackEvent("boot_complete");
    }

    private vfsText(path: string): string | null {
        const entry = this.vfs.lookup(this.vfs.resolvePath(path));
        if (entry?.type !== "file" || !entry.content) return null;
        return entry.content.trim();
    }

    private unknownCommandMessage(): string {
        return this.vfsText(".unknown.txt") ?? "Unknown command. Type 'help'.";
    }

    private shellPrompt(): string {
        return formatShellPrompt(
            this.vfs,
            this.appData.config.handle,
            this.appData.config.hostname,
        );
    }

    private layout(): void {
        this.cameras.main.setBackgroundColor(COLORS.bg);
        if (this.poweringUpText) {
            this.poweringUpText.setPosition(GAME_WIDTH / 2, GAME_HEIGHT / 2);
        }

        const promptTop = this.inputChrome.promptTop(this.scale.height, this.inputEnabled);
        this.inputChrome.drawBackdrop(this.scale.width, this.scale.height, promptTop);
        this.outputView.scrollPinBottom = true;
        this.outputView.scrollOutput();
        this.updatePrompt();
        this.inputChrome.layoutStatusBar(this.scale.width, this.scale.height);
    }

    private isPasswordMode(): boolean {
        return this.passwordConfig !== null;
    }

    private enterPasswordMode(config: PasswordPromptConfig): void {
        if (config.prelude) {
            this.outputView.appendLines(config.prelude, { color: COLORS.dim });
        }

        this.passwordConfig = config;
        this.passwordAttempts = 0;
        this.passwordBuffer = "";
        this.inputChrome.setPasswordPrompt(config.prompt);
        this.updatePrompt();
    }

    private exitPasswordMode(): void {
        this.passwordConfig = null;
        this.passwordAttempts = 0;
        this.passwordBuffer = "";
        this.inputChrome.resetPromptCache(this.shellPrompt());
        this.updatePrompt();
    }

    private submitPassword(): void {
        const config = this.passwordConfig;
        if (!config) return;

        this.outputView.appendLine(
            `${config.prompt}${"*".repeat(this.passwordBuffer.length)}`,
            { color: COLORS.dim },
            true,
        );

        this.passwordBuffer = "";
        this.passwordAttempts += 1;

        const style = { color: COLORS.dim };

        if (this.passwordAttempts < config.maxAttempts) {
            this.outputView.appendLine(config.retryLine, style);
            this.terminalAudio.playError();
            this.updatePrompt();
            return;
        }

        this.outputView.appendLines(config.failureLines, style);
        this.terminalAudio.playError();
        this.exitPasswordMode();
    }

    private cancelPasswordMode(): void {
        if (!this.passwordConfig) return;

        this.outputView.appendLine(`${this.passwordConfig.prompt}^C`, { color: COLORS.dim }, true);
        trackEvent("password_cancel");
        this.exitPasswordMode();
    }

    private updatePrompt(): void {
        const promptTop = this.inputChrome.promptTop(this.scale.height, this.inputEnabled);
        this.inputChrome.updatePrompt(
            promptTop,
            this.commandBuffer,
            this.passwordConfig,
            this.passwordBuffer,
        );
    }

    private triggerDownload(url: string, filename: string): void {
        trackFileDownload(filename);
        void triggerBrowserDownload(url, filename);
    }

    private runCommand(raw: string | Command, source: AnalyticsSource): void {
        handleTerminalCommand(
            {
                appData: this.appData,
                vfs: this.vfs,
                session: this.session,
                commands: this.commands,
                output: this.outputView,
                images: this.images,
                shellPrompt: () => this.shellPrompt(),
                unknownCommandMessage: () => this.unknownCommandMessage(),
                enterPasswordMode: (config) => this.enterPasswordMode(config),
                reboot: () => this.reboot(),
                clearScreen: () => this.clearScreen(),
                triggerDownload: (url, filename) => this.triggerDownload(url, filename),
                playError: () => this.terminalAudio.playError(),
                playClick: () => this.terminalAudio.playClick(),
                updatePrompt: () => this.updatePrompt(),
                onCommand: trackCommand,
                source,
            },
            raw,
        );
    }

    private onPasswordKeyDown(event: KeyboardEvent): void {
        if (event.ctrlKey && event.key.toLowerCase() === "c") {
            event.preventDefault();
            this.cancelPasswordMode();
            return;
        }

        if (event.key === "Enter") {
            event.preventDefault();
            this.terminalAudio.playKey();
            this.submitPassword();
            return;
        }

        if (event.key === "Backspace") {
            event.preventDefault();
            this.passwordBuffer = this.passwordBuffer.slice(0, -1);
            this.terminalAudio.playKey();
            this.updatePrompt();
            return;
        }

        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault();
            this.passwordBuffer += event.key;
            this.terminalAudio.playKey();
            this.updatePrompt();
        }
    }

    private onKeyDown(event: KeyboardEvent): void {
        if (!this.inputEnabled) {
            return;
        }

        if (this.isPasswordMode()) {
            this.onPasswordKeyDown(event);
            return;
        }

        if (event.ctrlKey && event.key.toLowerCase() === "l") {
            event.preventDefault();
            trackEvent("clear_screen");
            this.clearScreen();
            return;
        }

        if (event.key === "Backspace") {
            event.preventDefault();
            this.commandBuffer = this.commandBuffer.slice(0, -1);
            this.terminalAudio.playKey();
            this.updatePrompt();
            return;
        }

        if (event.key === "Enter") {
            event.preventDefault();
            this.terminalAudio.playKey();
            this.runCommand(this.commandBuffer, "typed");
            this.commandBuffer = "";
            this.updatePrompt();
            return;
        }

        if (event.key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
            event.preventDefault();
            this.commandBuffer += event.key;
            this.terminalAudio.playKey();
            this.updatePrompt();
        }
    }
}
